import React, { useState } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import {
   listDataPlans,
   createDataPlan,
   formatMb,
   planRemainingMb,
   planTotalMb,
   DataPlan,
   DataPlanMode,
} from '@/api/dataPlans';
import DataPlanModal from '@/components/Admin/Prompt/DataPlanModal';

// Top-level Data Plans page. Mirrors the Vendo / DeviceSettings visual
// language: PRIMARY blue for actions, theme-aware GRAY tokens, tabs and
// scrollable bodies for the per-plan modal (lives in DataPlanModal.tsx).
function DataPlans() {
   const qc = useQueryClient();
   const [createOpen, setCreateOpen] = useState(false);
   const [openPlanId, setOpenPlanId] = useState<string | null>(null);

   const { data: plans, isLoading } = useQuery({
      queryKey: ['data-plans'],
      queryFn: listDataPlans,
   });

   return (
      <Wrapper>
         <Header>
            <HeaderText>
               <Title>Data Plans</Title>
               <Sub>Track ISP quota and link it to vendos that share the same line.</Sub>
            </HeaderText>
            <NewBtn onClick={() => setCreateOpen(true)}>+ New Plan</NewBtn>
         </Header>

         {isLoading && <SkeletonLines lines={3} />}

         {!isLoading && (!plans || plans.length === 0) && (
            <Empty>
               <EmptyIcon>
                  {/* Open box / empty drawer — reads as "nothing in here yet" */}
                  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke={colors.GRAY3} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                     <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
                     <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                     <line x1="12" y1="22.08" x2="12" y2="12" />
                  </svg>
               </EmptyIcon>
               <EmptyTitle>No data plans yet</EmptyTitle>
               <EmptySub>
                  Add a plan for each ISP subscription you have, then link your vendos
                  so they share the same data quota.
               </EmptySub>
               <EmptyBtn onClick={() => setCreateOpen(true)}>+ Create your first plan</EmptyBtn>
            </Empty>
         )}

         {plans && plans.length > 0 && (
            <Grid>
               {plans.map((p) => (
                  <PlanCard key={p._id} plan={p} onOpen={() => setOpenPlanId(p._id)} />
               ))}
            </Grid>
         )}

         {createOpen && (
            <CreatePlanModal
               onClose={() => setCreateOpen(false)}
               onCreated={() => { qc.invalidateQueries({ queryKey: ['data-plans'] }); setCreateOpen(false); }}
            />
         )}

         {openPlanId && (
            <DataPlanModal
               planId={openPlanId}
               onClose={() => {
                  setOpenPlanId(null);
                  qc.invalidateQueries({ queryKey: ['data-plans'] });
               }}
            />
         )}
      </Wrapper>
   );
}

function PlanCard({ plan, onOpen }: { plan: DataPlan; onOpen: () => void }) {
   const remaining = planRemainingMb(plan);
   const total = planTotalMb(plan);
   const usedPct = total > 0 ? Math.min(100, (plan.totalUsedMb / total) * 100) : 0;
   const deviceCount = plan.devices.length;
   const rateCount = plan.rates.length;
   const lowPool = total > 0 && usedPct >= 85;

   return (
      <Card onClick={onOpen}>
         <CardTopRow>
            <CardName>{plan.name}</CardName>
            <ModePill $mode={plan.mode}>{plan.mode === 'shared' ? 'Shared' : 'Manual'}</ModePill>
         </CardTopRow>
         <CardIsp>{plan.isp || 'No ISP set'}</CardIsp>

         <ProgressLabel>
            <ProgressUsed>{formatMb(plan.totalUsedMb)} used</ProgressUsed>
            <ProgressRemaining $low={lowPool}>{formatMb(remaining)} left</ProgressRemaining>
         </ProgressLabel>
         <Bar>
            <BarFill $pct={usedPct} $low={lowPool} />
         </Bar>

         <CardFooter>
            <CardMeta>
               {deviceCount} {deviceCount === 1 ? 'vendo' : 'vendos'} · {rateCount} {rateCount === 1 ? 'rate' : 'rates'}
            </CardMeta>
            <SettingsHint>
               <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
               </svg>
               Settings
            </SettingsHint>
         </CardFooter>
      </Card>
   );
}

function CreatePlanModal({
   onClose, onCreated,
}: { onClose: () => void; onCreated: () => void }) {
   const [name, setName] = useState('');
   const [isp, setIsp] = useState('');
   const [mode, setMode] = useState<DataPlanMode>('shared');

   const create = useMutation({
      mutationFn: () => createDataPlan({ name, isp, mode }),
      onSuccess: () => { toast.success(`Created "${name}"`); onCreated(); },
      onError: (err: any) => {
         toast.error(err?.response?.data?.error || 'Could not create plan');
      },
   });

   const canSubmit = name.trim().length > 0 && !create.isPending;

   return (
      <Backdrop onClick={onClose}>
         <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHead>
               <ModalTitle>New Data Plan</ModalTitle>
               <CloseX onClick={onClose} aria-label="Close">×</CloseX>
            </ModalHead>

            <Field>
               <Lbl>Plan name</Lbl>
               <Inp value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Barangay Lagao Starlink" autoFocus />
            </Field>
            <Field>
               <Lbl>ISP <Optional>(optional)</Optional></Lbl>
               <Inp value={isp} onChange={(e) => setIsp(e.target.value)} placeholder="e.g. Starlink, Converge" />
            </Field>
            <Field>
               <Lbl>Pool mode</Lbl>
               <ModeRow>
                  <ModeOpt $active={mode === 'shared'} onClick={() => setMode('shared')}>
                     <ModeOptName>Shared pool</ModeOptName>
                     <ModeOptDesc>All linked vendos draw from one bucket. Self-balancing.</ModeOptDesc>
                  </ModeOpt>
                  <ModeOpt $active={mode === 'manual'} onClick={() => setMode('manual')}>
                     <ModeOptName>Manual allotment</ModeOptName>
                     <ModeOptDesc>You set how much each vendo gets. Hard per-vendo limits.</ModeOptDesc>
                  </ModeOpt>
               </ModeRow>
            </Field>

            <BtnRow>
               <CancelBtn onClick={onClose} disabled={create.isPending}>Cancel</CancelBtn>
               <PrimaryBtn onClick={() => create.mutate()} disabled={!canSubmit}>
                  {create.isPending ? 'Creating…' : 'Create plan'}
               </PrimaryBtn>
            </BtnRow>
         </Modal>
      </Backdrop>
   );
}

export default DataPlans;

// ---- Styled (theme-aware via CSS-var-backed color tokens) ----

const Wrapper = styled.div`
   padding: 24px 28px;
`;

const Header = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: flex-end;
   margin-bottom: 24px;
   gap: 16px;
`;

const HeaderText = styled.div``;

const Title = styled.h1`
   font-size: 22px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
   margin-bottom: 4px;
`;

const Sub = styled.p`
   font-size: 12px;
   color: ${colors.GRAY3};
`;

const NewBtn = styled.button`
   padding: 9px 16px;
   border-radius: 8px;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   font-size: 13px;
   font-weight: 600;
   white-space: nowrap;
   border: none;
   cursor: pointer;
   transition: opacity 0.15s;
   &:hover { opacity: 0.9; }
`;


// Empty state — no box/background. Centered vertically in the
// remaining viewport so it doesn't crowd the top.
const Empty = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;
   text-align: center;
   min-height: 60vh;
   padding: 24px;
   gap: 4px;
`;

const EmptyIcon = styled.div`
   margin-bottom: 18px;
   opacity: 0.75;
`;

const EmptyTitle = styled.h2`
   font-size: 15px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
   margin-bottom: 6px;
`;

const EmptySub = styled.p`
   font-size: 12px;
   color: ${colors.GRAY3};
   line-height: 1.6;
   margin-bottom: 18px;
`;

const EmptyBtn = styled.button`
   padding: 8px 16px;
   border-radius: 7px;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   font-size: 12px;
   font-weight: 600;
   border: none;
   cursor: pointer;
   transition: opacity 0.15s;
   &:hover { opacity: 0.9; }
`;

const Grid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
   gap: 14px;
`;

const Card = styled.div`
   background: var(--app-card-bg, transparent);
   border: 1px solid ${colors.GRAY12};
   border-radius: 12px;
   padding: 16px;
   cursor: pointer;
   transition: transform 0.1s;
   &:active { transform: scale(0.997); }
`;

const CardTopRow = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   gap: 8px;
   margin-bottom: 2px;
`;

const CardName = styled.h3`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const ModePill = styled.span<{ $mode: DataPlanMode }>`
   font-size: 9px;
   font-weight: 700;
   padding: 2px 8px;
   border-radius: 999px;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   background: ${(p) => p.$mode === 'shared' ? `${colors.PRIMARY}15` : `${colors.SUCCESS}15`};
   color: ${(p) => p.$mode === 'shared' ? colors.PRIMARY : colors.SUCCESS};
`;

const CardIsp = styled.div`
   font-size: 11px;
   color: ${colors.GRAY3};
   margin-bottom: 16px;
`;

const ProgressLabel = styled.div`
   display: flex;
   justify-content: space-between;
   font-size: 11px;
   margin-bottom: 6px;
`;

const ProgressUsed = styled.span`
   color: ${colors.GRAY3};
`;

const ProgressRemaining = styled.span<{ $low: boolean }>`
   color: ${(p) => p.$low ? colors.DANGER : colors.MAIN_DARK};
   font-weight: 600;
`;

const Bar = styled.div`
   height: 8px;
   background: ${colors.GRAY14};
   border-radius: 4px;
   overflow: hidden;
   margin-bottom: 14px;
`;

const BarFill = styled.div<{ $pct: number; $low: boolean }>`
   height: 100%;
   width: ${(p) => p.$pct}%;
   background: ${(p) => p.$low ? colors.DANGER : colors.PRIMARY};
   border-radius: 4px;
   transition: width 0.4s ease-out;
`;

const CardFooter = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   gap: 8px;
   padding-top: 10px;
   border-top: 1px solid ${colors.GRAY12};
`;

const CardMeta = styled.div`
   font-size: 11px;
   color: ${colors.GRAY3};
`;

const SettingsHint = styled.div`
   display: flex;
   align-items: center;
   gap: 5px;
   font-size: 11px;
   font-weight: 600;
   color: ${colors.GRAY3};
`;

// ---- Modal styled (theme-aware) ----

const Backdrop = styled.div`
   position: fixed;
   inset: 0;
   background: rgba(0,0,0,0.45);
   display: flex;
   align-items: center;
   justify-content: center;
   z-index: 100;
   padding: 20px;
`;

const Modal = styled.div`
   background: var(--app-surface, #fff);
   border: 1px solid ${colors.GRAY12};
   border-radius: 14px;
   padding: 22px 24px;
   max-width: 460px;
   width: 100%;
`;

const ModalHead = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   margin-bottom: 16px;
`;

const ModalTitle = styled.h2`
   font-size: 16px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const CloseX = styled.button`
   font-size: 22px;
   line-height: 1;
   color: ${colors.GRAY3};
   background: none;
   border: none;
   cursor: pointer;
   padding: 0 4px;
   &:hover { color: ${colors.MAIN_DARK}; }
`;

const Field = styled.div`
   margin-bottom: 12px;
`;

const Lbl = styled.label`
   display: block;
   font-size: 11px;
   font-weight: 600;
   color: ${colors.MAIN_DARK};
   margin-bottom: 5px;
`;

const Optional = styled.span`
   font-weight: 400;
   color: ${colors.GRAY3};
`;

const Inp = styled.input`
   width: 100%;
   padding: 9px 11px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 7px;
   font-size: 13px;
   color: ${colors.MAIN_DARK};
   background: var(--app-surface, #fff);
   &:focus { outline: none; border-color: ${colors.PRIMARY}; }
`;

const ModeRow = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 8px;
`;

const ModeOpt = styled.div<{ $active: boolean }>`
   border: 1.5px solid ${(p) => p.$active ? colors.PRIMARY : colors.GRAY12};
   background: ${(p) => p.$active ? `${colors.PRIMARY}10` : 'transparent'};
   border-radius: 8px;
   padding: 10px 12px;
   cursor: pointer;
   transition: all 0.15s;
`;

const ModeOptName = styled.div`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
   margin-bottom: 3px;
`;

const ModeOptDesc = styled.div`
   font-size: 10px;
   color: ${colors.GRAY3};
   line-height: 1.4;
`;

const BtnRow = styled.div`
   display: flex;
   gap: 8px;
   margin-top: 18px;
`;

const CancelBtn = styled.button`
   flex: 1;
   padding: 9px;
   border-radius: 7px;
   background: transparent;
   border: 1px solid ${colors.GRAY12};
   color: ${colors.GRAY3};
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const PrimaryBtn = styled.button`
   flex: 1;
   padding: 9px;
   border-radius: 7px;
   background: ${colors.PRIMARY};
   border: none;
   color: ${colors.WHITE};
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   transition: opacity 0.15s;
   &:hover { opacity: 0.9; }
   &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
